import { useCallback, useMemo } from 'react';
import pick from 'lodash/pick';

import { TeamUUID } from '../../../../teams/teamsTypes';
import {
  FetchProductsFilters,
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsSort,
  FetchProductsFilterProducts,
  ProductCategoryID
} from '../../../../products/productsTypes';
import { IdFilterType } from '../../../../../types';
import {
  CategoryID,
  CategoryLocalizedName
} from '../../../../categories/categoriesTypes';

import { useCacheProductsAggregations } from '../../../../products/hooks/useCacheProductsAggregations';

import { ThreeDStockShowFiltersLayout } from '../../layouts/ThreeDStockShowFiltersLayout';

import { ThreeDStockProductsFiltersAiTagsFilter } from './components/ThreeDStockProductsFiltersAiTagsFilter';
import { ThreeDStockProductsFiltersBlockedFilter } from './components/ThreeDStockProductsFiltersBlockedFilter';
import { ThreeDStockProductsFiltersRenderTypeFilter } from './components/ThreeDStockProductsFiltersRenderTypeFilter';
import { ThreeDStockProductsFiltersColorsFilter } from './components/ThreeDStockProductsFiltersColorsFilter';
import { ThreeDStockProductsFiltersImageFilterBadge } from './components/ThreeDStockProductsFiltersImageFilterBadge';
import { ThreeDStockProductsFiltersProductFilterBadge } from './components/ThreeDStockProductsFiltersProductFilterBadge';
import { ThreeDStockProductsFiltersAppliedFilters } from './components/ThreeDStockProductsFiltersAppliedFilters';
import { ThreeDStockProductsFiltersCategoriesFilter } from './components/ThreeDStockProductsFiltersCategoriesFilter';
import { ThreeDStockProductsFiltersFavoritesFilter } from './components/ThreeDStockProductsFiltersFavoritesFilter';
import { ThreeDStockProductsFiltersBrandsFilter } from './components/ThreeDStockProductsFiltersBrandsFilter';
import { ThreeDStockProductsFiltersSeasonalFilter } from './components/ThreeDStockProductsFiltersSeasonalFilter';
import { ThreeDStockProductsFiltersTypesFilter } from './components/ThreeDStockProductsFiltersTypesFilter';
import { ThreeDStockProductsFiltersCollectionsFilter } from './components/ThreeDStockProductsFiltersCollectionsFilter';
import { ThreeDStockProductsFiltersSearchByNameFilter } from './components/ThreeDStockProductsFiltersSearchByNameFilter';

import { Translate } from '../../../../../helpers/Translate';
import { LoadingSkeleton } from '../../../../../helpers/LoadingSkeleton';
import { PureIconButtonHelper } from '../../../../../helpers/buttons/PureIconButtonHelper';
import { CheckPermissions } from '../../../../../helpers/CheckPermissions';

import { ProductsPermissions } from '../../../../products/productsConstants';

import { IconsEnum } from '../../../../../assets/icons/types';

import { words } from '../../../../../locales/keys';

const productsFiltersFields = [
  FetchProductsFilterFields.TEAM_IDS,
  FetchProductsFilterFields.CLIENT_IDS,
  FetchProductsFilterFields.NDA,
  FetchProductsFilterFields.FAVORITE,
  FetchProductsFilterFields.BLOCKED,
  FetchProductsFilterFields.CURRENT_TEAM_FAVORITE,
  FetchProductsFilterFields.TEAM_ID_FAVORITE,
  FetchProductsFilterFields.PRODUCT_CATEGORY_ID,
  FetchProductsFilterFields.PRODUCT_PARENT_CATEGORY_IDS,
  FetchProductsFilterFields.NAME_SKU_TAGS,
  FetchProductsFilterFields.WITH_TASK_NAME,
  FetchProductsFilterFields.SIMILAR_IMAGE,
  FetchProductsFilterFields.SIMILAR_PRODUCT,
  FetchProductsFilterFields.SIMILAR_PRODUCTS_BY_CUSTOM_AREA,
  FetchProductsFilterFields.SEARCH_BY_DETECTED_ID,
  FetchProductsFilterFields.SEASONAL_ID,
  FetchProductsFilterFields.PRODUCT_TYPE_ID
];

interface ThreeDStockProductsFiltersProps {
  teamUuid?: TeamUUID;
  productsFilters: FetchProductsFilters;
  defaultProductCategoryId?: IdFilterType;
  defaultProductParentCategoryIds?: ProductCategoryID[];
  changeProductsFilters: ChangeProductsFiltersFunc;
  filterProducts: FetchProductsFilterProducts;
  sortProducts?: (nextSort: FetchProductsSort) => void;
  closeFilters?: () => void;
  clearFilters?: (persistFiltersFields?: FetchProductsFilterFields[]) => void;
  checkedCategories: { id: CategoryID; localizedName: CategoryLocalizedName }[];
  setCheckedCategories: (
    categories: { id: CategoryID; localizedName: CategoryLocalizedName }[]
  ) => void;
  changeCategory: (
    changeFilters?: FetchProductsFilters,
    removeFilters?: string[]
  ) => void;
  withTypeFiler?: boolean;
  withSeasonFilter?: boolean;
  withSearchByNameFilter?: boolean;
  isModal?: boolean;
  isCompact?: boolean;
  withoutCategoryDrilldown?: boolean;
}

function ThreeDStockProductsFilters({
  teamUuid,
  productsFilters,
  defaultProductCategoryId,
  defaultProductParentCategoryIds,
  changeProductsFilters,
  filterProducts,
  sortProducts,
  closeFilters,
  clearFilters,
  checkedCategories,
  setCheckedCategories,
  changeCategory,
  withSeasonFilter,
  withTypeFiler,
  withSearchByNameFilter,
  isModal,
  isCompact,
  withoutCategoryDrilldown
}: ThreeDStockProductsFiltersProps) {
  const handleClearFilters = useCallback<() => void>(() => {
    clearFilters?.();
    setCheckedCategories([]);
  }, [clearFilters, setCheckedCategories]);

  const handleChangeCategories = useCallback<ChangeProductsFiltersFunc>(
    (changedFilters, removeFilters) => {
      clearFilters?.([
        FetchProductsFilterFields.SIMILAR_PRODUCT,
        FetchProductsFilterFields.SIMILAR_IMAGE,
        FetchProductsFilterFields.NAME_SKU_TAGS,
        FetchProductsFilterFields.NAME
      ]);
      changeProductsFilters(changedFilters, removeFilters);
    },
    [changeProductsFilters, clearFilters]
  );

  const productsFiltersForAiTags = useMemo<FetchProductsFilters>(
    () => pick(productsFilters, productsFiltersFields),
    [productsFilters]
  );

  const { productsAggregations, productsAggregationsFetched } =
    useCacheProductsAggregations({
      productsFilters: productsFiltersForAiTags
    });

  return (
    <ThreeDStockShowFiltersLayout onClose={closeFilters} isModal={isModal}>
      <div className="h-full flex flex-col">
        <div className="flex justify-between pl-4 pr-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 py-1.5 sticky lg:static top-0 z-20 flex-grow-0">
          <div className="text-sm uppercase font-semibold leading-8">
            <Translate id={words.refineSearch} />
          </div>

          <PureIconButtonHelper
            className="py-1.5 pl-1.5 pr-1.5 rounded-full inline-flex items-center leading-5 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 text-gray-500 dark:text-gray-400 transition-colors duration-150 ease-out"
            onClick={closeFilters}
            icon={IconsEnum.X}
            iconClassName="h-5 w-5"
            dataGa="close-filters"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          <div id="filters-products">
            <ThreeDStockProductsFiltersImageFilterBadge
              changeProductsFilters={changeProductsFilters}
              productsFilters={productsFilters}
              sortProducts={sortProducts}
            />
            <ThreeDStockProductsFiltersProductFilterBadge
              changeProductsFilters={changeProductsFilters}
              productsFilters={productsFilters}
              sortProducts={sortProducts}
            />

            <ThreeDStockProductsFiltersAppliedFilters
              changeProductsFilters={changeProductsFilters}
              productsFilters={productsFilters}
              filterProducts={filterProducts}
              clearFilters={handleClearFilters}
              checkedCategories={checkedCategories}
              setCheckedCategories={setCheckedCategories}
              defaultProductCategoryId={defaultProductCategoryId}
              defaultProductParentCategoryIds={defaultProductParentCategoryIds}
              productsAggregations={productsAggregations}
            />

            {withSearchByNameFilter && (
              <CheckPermissions
                action={ProductsPermissions.READ_PRODUCTS_NAME_FILTER}
              >
                <ThreeDStockProductsFiltersSearchByNameFilter
                  productsFilters={productsFilters}
                  changeProductsFilters={changeProductsFilters}
                  isCompact={isCompact}
                />
              </CheckPermissions>
            )}

            <CheckPermissions
              action={ProductsPermissions.READ_PRODUCTS_CATEGORIES_FILTER}
            >
              <ThreeDStockProductsFiltersCategoriesFilter
                changeProductsFilters={handleChangeCategories}
                productsFilters={productsFilters}
                defaultProductCategoryId={defaultProductCategoryId}
                checkedCategories={checkedCategories}
                setCheckedCategories={setCheckedCategories}
                changeCategory={changeCategory}
                isCompact={isCompact}
                withoutCategoryDrilldown={withoutCategoryDrilldown}
              />
            </CheckPermissions>

            <CheckPermissions
              action={ProductsPermissions.READ_PRODUCTS_COLLECTIONS_FILTER}
            >
              <ThreeDStockProductsFiltersCollectionsFilter
                changeProductsFilters={changeProductsFilters}
                productsAggregations={productsAggregations}
                productsFilters={productsFilters}
              />
            </CheckPermissions>

            <ThreeDStockProductsFiltersFavoritesFilter
              teamUuid={teamUuid}
              changeProductsFilters={changeProductsFilters}
              productsFilters={productsFilters}
              isCompact={isCompact}
            />
            <LoadingSkeleton loaded={productsAggregationsFetched}>
              <CheckPermissions
                action={
                  ProductsPermissions.READ_PRODUCTS_AI_MATERIAL_TAGS_FILTER
                }
              >
                <ThreeDStockProductsFiltersAiTagsFilter
                  changeProductsFilters={changeProductsFilters}
                  fieldName={FetchProductsFilterFields.AI_MATERIAL_TAGS}
                  productsAggregations={productsAggregations}
                  productsFilters={productsFilters}
                  isCompact={isCompact}
                />
              </CheckPermissions>

              <ThreeDStockProductsFiltersBrandsFilter
                changeProductsFilters={changeProductsFilters}
                productsAggregations={productsAggregations}
                productsFilters={productsFilters}
                isCompact={isCompact}
              />

              <CheckPermissions
                action={
                  ProductsPermissions.READ_PRODUCTS_AI_INTERIOR_STYLE_TAGS_FILTER
                }
              >
                <ThreeDStockProductsFiltersAiTagsFilter
                  changeProductsFilters={changeProductsFilters}
                  fieldName={FetchProductsFilterFields.AI_INTERIOR_STYLE_TAGS}
                  productsAggregations={productsAggregations}
                  productsFilters={productsFilters}
                  isCompact={isCompact}
                />
              </CheckPermissions>

              <CheckPermissions
                action={
                  ProductsPermissions.READ_PRODUCTS_AI_ROOM_CLASSIFICATION_TAGS_FILTER
                }
              >
                <ThreeDStockProductsFiltersAiTagsFilter
                  changeProductsFilters={changeProductsFilters}
                  fieldName={
                    FetchProductsFilterFields.AI_ROOM_CLASSIFICATION_TAGS
                  }
                  productsAggregations={productsAggregations}
                  productsFilters={productsFilters}
                  isCompact={isCompact}
                />
              </CheckPermissions>

              <CheckPermissions
                action={
                  ProductsPermissions.READ_PRODUCTS_AI_FEATURE_TAGS_FILTER
                }
              >
                <ThreeDStockProductsFiltersAiTagsFilter
                  changeProductsFilters={changeProductsFilters}
                  fieldName={FetchProductsFilterFields.AI_FEATURE_TAGS}
                  productsAggregations={productsAggregations}
                  productsFilters={productsFilters}
                  isCompact={isCompact}
                />
              </CheckPermissions>

              <CheckPermissions
                action={
                  ProductsPermissions.READ_PRODUCTS_AI_MAIN_COLOR_TAGS_FILTER
                }
              >
                <ThreeDStockProductsFiltersColorsFilter
                  changeProductsFilters={changeProductsFilters}
                  productsAggregations={productsAggregations}
                  productsFilters={productsFilters}
                />
              </CheckPermissions>

              <CheckPermissions
                action={ProductsPermissions.READ_PRODUCTS_RENDER_TYPE_FILTER}
              >
                <ThreeDStockProductsFiltersRenderTypeFilter
                  productsFilters={productsFilters}
                  changeProductsFilters={changeProductsFilters}
                />
              </CheckPermissions>

              <CheckPermissions
                action={ProductsPermissions.READ_PRODUCTS_BLOCKED_FILTER}
              >
                <ThreeDStockProductsFiltersBlockedFilter
                  productsFilters={productsFilters}
                  changeProductsFilters={changeProductsFilters}
                />
              </CheckPermissions>
            </LoadingSkeleton>
            {withTypeFiler && (
              <ThreeDStockProductsFiltersTypesFilter
                changeProductsFilters={changeProductsFilters}
                productsFilters={productsFilters}
              />
            )}
            {withSeasonFilter && (
              <ThreeDStockProductsFiltersSeasonalFilter
                changeProductsFilters={changeProductsFilters}
                productsFilters={productsFilters}
              />
            )}
          </div>
        </div>
      </div>
    </ThreeDStockShowFiltersLayout>
  );
}

export default ThreeDStockProductsFilters;
